import { createReducer, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { DatasetShareSetting, AvailableSubscription, ExportTemplate } from './dataset.model';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import {
  dataTypes,
  SchemaField,
  getDefaultSchemaFields,
  SystemFieldType,
  DataClassification,
  DocumentSchema
} from '../../../core/store/models/document.model';
import * as DatasetActions from './dataset.actions';
import { QueryParams, queryParamsFromResult } from 'src/app/modules/core/store/models/query.model';
import { v4 as uuidv4 } from 'uuid';
import { GUID_EMPTY } from '@app/core/constants';
import produce from 'immer';

export const datafeedsFeatureKey = 'datafeeds';

export interface State extends EntityState<Dataset> {
  queryParams?: QueryParams;
  queryParamsList?: QueryParams;
  selectedDatasetUId?: string | null;
  selectedDatasetUIdForDelete?: string | null;
  selectedDatasetFieldUId?: string | null;
  selectedDatasetFieldUIdForDelete?: string | null;
  datasetsForSelect?: Dataset[];
  shareSettings?: {
    queryParams?: QueryParams;
    settings?: DatasetShareSetting[];
  };
  availableSubscriptions?: {
    queryParams?: QueryParams;
    subscriptions?: AvailableSubscription[];
  };
  selectedTemplate?: ExportTemplate;
  exportTemplates?: ExportTemplate[];
  err?: any;
  ui?: {
    showDatasetProperties?: boolean;
    isDatasetPropertiesInEditMode?: boolean;
    editDatasetSection?: string;
    showNewFieldSelect?: boolean;
    datasetDirty?: boolean;
    isWorking?: false;
    showTemplateProperties?: boolean;
  };
  datasetSchemaWithTranslations?: DocumentSchema;
}

export const adapter: EntityAdapter<Dataset> = createEntityAdapter<Dataset>({
  selectId: (datafeed: Dataset) => datafeed.uId
});

export const initialState: State = adapter.getInitialState({
  queryParams: {
    skip: 0,
    take: 10,
    totalCount: 0,
    sortField: 'Name',
    sortDirection: 'Ascending'
  },

  queryParamsList: {
    skip: 0,
    take: 10,
    totalCount: 0,
    sortField: 'Name',
    sortDirection: 'Ascending'
  },
  selectedDatasetUId: null,
  selectedDatasetUIdForDelete: null,
  selectedDatasetFieldUId: null,
  selectedDatasetFieldUIdForDelete: null,
  exportTemplates: [],
  selectedTemplate: null,
  err: null,
  ui: {
    showDatasetProperties: false,
    isDatasetPropertiesInEditMode: false,
    editDatasetSection: null,
    showNewFieldSelect: false,
    datasetDirty: false,
    isWorking: false
  },
  datasetSchemaWithTranslations: null
});

export const reducer = createReducer(
  initialState,
  on(DatasetActions.addDataset, (state, action) => adapter.addOne(action.dataset, state)),
  on(DatasetActions.updateDatasetComplete, (state, action) =>
    adapter.updateOne(action.dataset, {
      ...state,
      ui: {
        datasetDirty: false,
        showDatasetProperties: false,
        isDatasetPropertiesInEditMode: false
      }
    })
  ),
  on(DatasetActions.deleteDataset, (state, action) => adapter.removeOne(action.uId, state)),
  on(DatasetActions.loadDatasets, (state, action) =>
    adapter.setAll(action.results.data, {
      ...state,
      queryParams: queryParamsFromResult(action.results),
      queryParamsList: queryParamsFromResult(action.results),
      selectedDatasetUId: null,
      selectedDatasetUIdForDelete: null,
      err: null
    })
  ),
  on(DatasetActions.newDataset, (state, action) => {
    const newDataset: Dataset = {
      uId: GUID_EMPTY,
      name: '',
      defaultColor: '#03A9F4',
      opacity: 1,
      iconPath: null,
      isCustomIcon: false,
      createdOn: null,
      updatedOn: null,
      lastEditedByName: null,
      lastEditedById: null,
      isActive: true,
      schema: {
        fields: getDefaultSchemaFields()
      },
      type: DatasetType.Alerts,
      subtype: DatasetSubtype.None,
      collaborators: [],
      hasTranslations: false,
      documentRetentionMonths: 0
    };

    return adapter.addOne(newDataset, {
      ...state,
      selectedDatasetUId: GUID_EMPTY,
      err: null,
      ui: {
        showDatasetProperties: true,
        isDatasetPropertiesInEditMode: false
      }
    });
  }),
  on(DatasetActions.setSelectedDatasetComplete, (state, action) => {
    return {
      ...state,
      selectedDatasetUId: action.uId,
      err: null,
      ui: {
        ...state.ui,
        editDatasetSection: 'fields'
      }
    };
  }),
  on(DatasetActions.showDatasetProperties, (state, action) => {
    return {
      ...state,
      ui: {
        showDatasetProperties: true,
        isDatasetPropertiesInEditMode: action.isEditMode
      }
    };
  }),
  on(DatasetActions.hideDatasetProperties, (state, action) => {
    return {
      ...state,
      ui: {
        showDatasetProperties: false,
        isDatasetPropertiesInEditMode: false
      }
    };
  }),
  on(DatasetActions.setDatasetForDelete, (state, action) => {
    return {
      ...state,
      selectedDatasetUIdForDelete: action.uId
    };
  }),
  on(DatasetActions.clearDatasetForDelete, (state, action) => {
    return {
      ...state,
      selectedDatasetUIdForDelete: null
    };
  }),
  on(DatasetActions.setSelectedEditDatasetSection, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        editDatasetSection: action.sectionName
      }
    };
  }),
  on(DatasetActions.showNewFieldSelect, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showNewFieldSelect: true
      }
    };
  }),
  on(DatasetActions.hideNewFieldSelect, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showNewFieldSelect: false
      }
    };
  }),
  on(DatasetActions.setIsDatasetDirty, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        datasetDirty: action.isDirty
      }
    };
  }),
  on(DatasetActions.addDatasetField, (state, action) => {
    const existingDataset = state.entities[state.selectedDatasetUId];

    let existingFields = existingDataset?.schema?.fields;
    if (!existingFields) {
      existingFields = [];
    }

    //
    // create a default name of the field ensure the name is unique
    // the user may change it later
    //
    const defaultName = 'New Field';
    let fieldName = defaultName;
    let index = 0;
    let goodName = false;
    while (!goodName) {
      goodName = true;

      for (const field of existingFields) {
        if (field.displayName === fieldName) {
          index++;
          fieldName = `${defaultName} ${index}`;
          goodName = false;
          break;
        }
      }
    }

    const newField: SchemaField = {
      uId: uuidv4(),
      systemName: null,
      displayName: fieldName,
      displayNameCulture: action.cultureCode,
      dataType: action.dataType,
      fieldType: action.fieldType, //SystemFieldType.Editable + SystemFieldType.Deletable,
      isUniqueKey: false,
      settings: {},
      picklistValues: [],
      enumerationValues: [],   
      dataClassification: DataClassification.Internal
    };

    const schemaWithTranslations = produce(state.datasetSchemaWithTranslations, draftState => {
      draftState.fields.push(newField);
    });

    const updatedDataset: Dataset = {
      ...existingDataset,
      schema: {
        fields: [...existingFields, newField]
      }
    };

    return adapter.setOne(updatedDataset, {
      ...state,
      selectedDatasetFieldUId: newField.uId,
      ui: {
        ...state.ui,
        showNewFieldSelect: false,
        datasetDirty: true
      },
      datasetSchemaWithTranslations: schemaWithTranslations
    });
  }),
  on(DatasetActions.cancelEditDatasetComplete, (state, action) => {
    return {
      ...state,
      selectedDatasetUId: null,
      selectedDatasetUIdForDelete: null,
      err: null,
      ui: {
        ...state.ui,
        showDatasetProperties: false,
        isDatasetPropertiesInEditMode: false,
        showNewFieldSelect: false,
        datasetDirty: false
      }
    };
  }),
  on(DatasetActions.cancelEditDatasetField, (state, action) => {
    return {
      ...state,
      selectedDatasetFieldUId: null
    };
  }),
  on(DatasetActions.setSelectedDatasetField, (state, action) => {
    return {
      ...state,
      selectedDatasetFieldUId: action.uId
    };
  }),
  on(DatasetActions.setSelectedDatasetFieldForDelete, (state, action) => {
    return {
      ...state,
      selectedDatasetFieldUIdForDelete: action.uId
    };
  }),
  on(DatasetActions.cancelDeleteDatasetField, (state, action) => {
    return {
      ...state,
      selectedDatasetFieldUIdForDelete: null
    };
  }),
  on(DatasetActions.deleteDatasetField, (state, action) => {
    const dataset = getSelectedDataset(state);

    if (!dataset) {
      return state;
    }

    let index = dataset.schema.fields.findIndex(f => f.uId === state.selectedDatasetFieldUIdForDelete);

    const updatedFields = [...dataset.schema.fields];
    updatedFields.splice(index, 1);

    index = state.datasetSchemaWithTranslations.fields.findIndex(f => f.uId === state.selectedDatasetFieldUIdForDelete);

    const updatedSchemaFields = [...state.datasetSchemaWithTranslations.fields];
    if (index >= 0) {
      updatedSchemaFields.splice(index, 1);
    }

    const updatedDataset = {
      ...dataset,
      schema: {
        ...dataset.schema,
        fields: updatedFields
      }
    };

    return adapter.setOne(updatedDataset, {
      ...state,
      selectedDatasetFieldUIdForDelete: null,
      ui: {
        ...state.ui,
        datasetDirty: true
      },
      datasetSchemaWithTranslations: {
        ...state.datasetSchemaWithTranslations,
        fields: updatedSchemaFields
      }
    });
  }),
  on(DatasetActions.updateDatasetField, (state, action) => {
    const dataset = getSelectedDataset(state);

    if (!dataset) {
      return state;
    }

    let index = state.datasetSchemaWithTranslations.fields.findIndex(f => f.uId === state.selectedDatasetFieldUId);
    const updatedSchemaFields = produce(state.datasetSchemaWithTranslations.fields, draftState => {
      if (index >= 0) {
        draftState[index] = action.field;
      }
    });

    index = dataset.schema.fields.findIndex(f => f.uId === state.selectedDatasetFieldUId);
    const updatedFields = produce(dataset.schema.fields, draftState => {
      if (index >= 0) {
        draftState[index] = action.field;
      }
    });

    const updatedDataset = {
      ...dataset,
      schema: {
        fields: updatedFields
      }
    };

    return adapter.setOne(updatedDataset, {
      ...state,
      selectedDatasetFieldUId: null,
      ui: {
        ...state.ui,
        datasetDirty: true
      },
      datasetSchemaWithTranslations: {
        ...state.datasetSchemaWithTranslations,
        fields: updatedSchemaFields
      }
    });
  }),

  on(DatasetActions.updateDatasetFields, (state, action) => {
    const dataset = getSelectedDataset(state);

    if (!dataset) {
      return state;
    }

    const updatedSchemaFields = [...state.datasetSchemaWithTranslations.fields];
    action.fields.forEach(fld => {
      const index = state.datasetSchemaWithTranslations.fields.findIndex(f => f.uId === fld.uId);
      if (index >= 0) {
        updatedSchemaFields[index] = fld;
      }
    });

    const updatedFields = [...dataset.schema.fields];
    action.fields.forEach(fld => {
      const index = dataset.schema.fields.findIndex(f => f.uId === fld.uId);
      if (index >= 0) {
        updatedFields[index] = fld;
      }
    });

    const updatedDataset = {
      ...dataset,
      schema: {
        fields: updatedFields
      }
    };

    return adapter.setOne(updatedDataset, {
      ...state,
      ui: {
        ...state.ui,
        datasetDirty: true
      },
      datasetSchemaWithTranslations: {
        ...state.datasetSchemaWithTranslations,
        fields: updatedSchemaFields
      }
    });
  }),

  on(DatasetActions.updateSelectedDatasetMappings, (state, action) => {
    const dataset = getSelectedDataset(state);

    if (!dataset) {
      return state;
    }

    const updatedDataset = {
      ...dataset,
      mappingsFrom: action.mappings
    };

    return adapter.setOne(updatedDataset, {
      ...state,
      ui: {
        ...state.ui,
        datasetDirty: true
      }
    });
  }),

  on(DatasetActions.loadDatasetsForSelectComplete, (state, action) => {
    return {
      ...state,
      datasetsForSelect: action.results.data
    };
  }),

  on(DatasetActions.loadDatasetSchemaWithTranslationsComplete, (state, action) => {
    return {
      ...state,
      datasetSchemaWithTranslations: action.schema
    };
  }),
  on(DatasetActions.getDatasetShareSettingsFromApiComplete, (state, action) => {
    return {
      ...state,
      shareSettings: {
        ...state.shareSettings,
        queryParams: {
          skip: action.results?.skip,
          take: action.results?.take,
          where: action.results?.where,
          sortField: action.results?.sortField,
          sortDirection: action.results?.sortDirection,
          sort: action.results?.sort,
          include: null,
          totalCount: action.results?.totalCount
        },
        settings: action.results?.data
      }
    };
  }),
  on(DatasetActions.getAvailableSubscriptionsFromApiComplete, (state, action) => {
    return {
      ...state,
      availableSubscriptions: {
        queryParams: {
          skip: action?.results?.skip,
          take: action?.results?.take,
          where: action?.results?.where,
          sortField: action?.results?.sortField,
          sortDirection: action?.results?.sortDirection,
          sort: action?.results?.sort,
          include: action?.results?.include,
          totalCount: action?.results?.totalCount
        },
        subscriptions: action.results?.data
      }
    };
  }),

  on(DatasetActions.loadDatasetExportTemplatesComplete, (state, action) => {
    return {
      ...state,
      exportTemplates: action.results?.data
    };
  }),

  on(DatasetActions.createExportTemplateComplete, (state, action) => {
    const exportTemplates = produce(state.exportTemplates, draftState => {
      draftState.push(action.template);
    });

    return {
      ...state,
      selectedTemplate: action.template,
      exportTemplates: exportTemplates
    };
  }),
  on(DatasetActions.updateExportTemplateComplete, (state, action) => {
    const exportTemplates = produce(state.exportTemplates, draftState => {
      const index = draftState.findIndex(d => d.uId === action.template.uId);
      if (index >= 0) {
        draftState.splice(index, 1, action.template);
      }
    });

    return {
      ...state,
      selectedTemplate: action.template,
      exportTemplates: exportTemplates
    };
  }),
  on(DatasetActions.deleteExportTemplateComplete, (state, action) => {
    const exportTemplates = produce(state.exportTemplates, draftState => {
      const index = draftState.findIndex(d => d.uId === action.template.uId);
      if (index >= 0) {
        draftState.splice(index, 1);
      }
    });

    return {
      ...state,
      exportTemplates: exportTemplates
    };
  }),

  on(DatasetActions.showTemplateProperties, (state, action) => {
    return {
      ...state,
      selectedTemplate: action.template,
      ui: {
        ...state.ui,
        showTemplateProperties: true
      }
    };
  }),

  on(DatasetActions.hideTemplateProperties, (state, action) => {
    return {
      ...state,
      selectedTemplate: null,
      ui: {
        ...state.ui,
        showTemplateProperties: false
      }
    };
  }),
  on(DatasetActions.loadSelectedDataset, (state, { dataset }) => {
    return adapter.upsertOne(dataset, {
      ...state,
      selectedDatasetUId: dataset.uId
    });
  })
);

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const getPinImages = (state: State) => [
  ['call_made', 'star_rate', 'person', 'home', 'where_to_vote', 'apartment', 'directions_boat'],
  ['flight_takeoff', 'attach_money', 'error', 'videocam', 'drive_eta', 'visibility', 'directions_run']
];

export const getQueryParams = (state: State) => state?.queryParams;
export const getQueryParamsList = (state: State) => state?.queryParamsList;

export const getShowDatasetProperties = (state: State) => state?.ui?.showDatasetProperties;

export const getIsDatasetPropertiesInEditMode = (state: State) => state?.ui?.isDatasetPropertiesInEditMode;

export const getShowNewFieldSelect = (state: State) => state?.ui?.showNewFieldSelect;

export const getDataTypes = (state: State) => dataTypes;

export const getDatasetDirty = (state: State) => state?.ui?.datasetDirty;

export const getDatasetFields = (state: State) => {
  if (!state.selectedDatasetUId) {
    return null;
  }

  const dataset = state.entities[state.selectedDatasetUId];
  if (!dataset) {
    return null;
  }

  const fields = dataset?.schema?.fields?.filter(f => (f.fieldType & SystemFieldType.Hidden) !== SystemFieldType.Hidden) || [];

  return fields;
};

export const getEditDatasetSection = (state: State) => {
  const section = state?.ui?.editDatasetSection;

  if (!section) {
    return 'fields';
  }

  return section;
};

export const getDatasetForDelete = (state: State) => {
  const uId = state?.selectedDatasetUIdForDelete;
  if (!uId) {
    return null;
  }

  return state.entities[uId];
};

export const getSelectedDataset = (state: State) => {
  const uId = state?.selectedDatasetUId;
  if (!uId) {
    return null;
  }

  return state.entities[uId];
};

export const getSelectedDatasetField = (state: State) => {
  const fieldUId = state?.selectedDatasetFieldUId;
  if (!fieldUId) {
    return null;
  }

  const dataset = getSelectedDataset(state);

  const field = dataset?.schema?.fields?.find(s => s.uId === fieldUId);

  return field;
};

export const getSelectedDatasetFieldForDelete = (state: State) => state?.selectedDatasetFieldUIdForDelete;

export const getDatasetsForSelect = (state: State) => state?.datasetsForSelect;

export const getSelectedDatasetUId = (state: State) => state?.selectedDatasetUId;

export const getShareSettingsQueryParams = (state: State) => state?.shareSettings?.queryParams;

export const getShareSettings = (state: State) => state?.shareSettings?.settings;

export const getAvailableSubscriptionQueryParams = (state: State) => state?.availableSubscriptions?.queryParams;

export const getAvailableSubscriptions = (state: State) => state?.availableSubscriptions?.subscriptions;

export const getExportTemplates = (state: State) => state?.exportTemplates;

export const getShowTemplateProperties = (state: State) => state?.ui.showTemplateProperties;

export const getSelectedTemplate = (state: State) => state?.selectedTemplate;

export const getDatasetSchemaWithTranslations = (state: State) => state?.datasetSchemaWithTranslations;

export const getSelectedDatasetFieldWithTranslations = (state: State) => {
  const fieldUId = state?.selectedDatasetFieldUId;
  if (!fieldUId) {
    return null;
  }

  let field = state.datasetSchemaWithTranslations.fields.find(s => s.uId === fieldUId);
  if (!!!field) {
    const dataset = getSelectedDataset(state);
    field = dataset?.schema?.fields?.find(s => s.uId === fieldUId);
  }

  return field;
};
